<template>
  <div
    class="drum-picker-container"
    :class="{ horizontal: orientation === 'horizontal' }"
    tabindex="0"
  >
    <div
      class="drum-picker"
      ref="drumRef"
      @scroll="onScroll"
      @wheel="onWheel"
      @mousedown="onMouseDown"
      :class="{ dragging: isDragging }"
    >
      <div
        v-for="(item, index) in extendedItems"
        :key="`item-${index}`"
        :data-index="index"
        class="drum-item"
        :class="{
          'drum-item-selected': selectedIndex === index,
          'drum-item-above': index === selectedIndex - 1,
          'drum-item-below': index === selectedIndex + 1,
        }"
        @click="selectItem(index)"
      >
        {{ item.label }}
        <span class="seletced-value-indicator">
          {{ selectedIndex === index ? props.unit : "" }}
        </span>
      </div>
    </div>
    <div class="drum-selection-indicator"></div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

interface DrumItem {
  value: number | string;
  label: string;
}

interface Props {
  modelValue: number | string | null;
  items: DrumItem[];
  orientation?: "vertical" | "horizontal";
  unit?: string;
}

interface Emits {
  (e: "update:modelValue", value: number | string | null): void;
}

const props = withDefaults(defineProps<Props>(), {
  orientation: "vertical",
  modelValue: null,
  unit: "см",
});

const emit = defineEmits<Emits>();

const drumRef = ref<HTMLElement>();
const selectedIndex = ref(0);
const scrollTimeout = ref<ReturnType<typeof window.setTimeout> | null>(null);
const isUserScrolling = ref(false);
const isProgrammaticScroll = ref(false);
const offset = 3;
const isDragging = ref(false);
const startPos = ref(0);
const startScrollPos = ref(0);
const inputBuffer = ref("");
const bufferResetTimeout = ref<ReturnType<typeof window.setTimeout> | null>(null);
const BUFFER_RESET_MS = 1500;

const extendedItems = computed(() => {
  const empty = { value: "", label: "" };
  return [...Array(offset).fill(empty), ...props.items, ...Array(offset).fill(empty)];
});

function scrollIndexIntoView(targetIndex: number) {
  if (!drumRef.value) return;
  const container = drumRef.value;
  const isVertical = props.orientation === "vertical";
  const item = container.querySelector(
    `.drum-item[data-index="${targetIndex}"]`
  ) as HTMLElement | null;

  const performScroll = () => {
    const containerRect = container.getBoundingClientRect();
    const containerCenter = isVertical
      ? containerRect.top + containerRect.height / 2
      : containerRect.left + containerRect.width / 2;

    const currentScroll = isVertical ? container.scrollTop : container.scrollLeft;
    let scrollPos: number;

    if (item) {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = isVertical
        ? itemRect.top + itemRect.height / 2
        : itemRect.left + itemRect.width / 2;
      const delta = itemCenter - containerCenter;
      scrollPos = currentScroll + delta;
    } else {
      const itemSize = isVertical ? 50 : 46;
      const containerSize = isVertical ? container.clientHeight : container.clientWidth;
      scrollPos = targetIndex * itemSize - (containerSize - itemSize) / 2;
    }

    const scrollOptions = {
      [isVertical ? "top" : "left"]: scrollPos,
      behavior: "auto" as ScrollBehavior, // Убираем smooth для лучшей производительности
    };

    // Используем requestAnimationFrame для синхронизации с рендерингом
    requestAnimationFrame(() => {
      container.scrollTo(scrollOptions);
    });
  };

  requestAnimationFrame(performScroll);
}

function programmaticScrollTo(targetIndex: number) {
  if (!drumRef.value) return;

  // Update selectedIndex and emit if it's a real item
  const realIndex = targetIndex - offset;
  if (realIndex >= 0 && realIndex < props.items.length) {
    selectedIndex.value = targetIndex;
    emit("update:modelValue", props.items[realIndex].value);
  }

  isProgrammaticScroll.value = true;

  nextTick(() => {
    scrollIndexIntoView(targetIndex);
    setTimeout(() => {
      isProgrammaticScroll.value = false;
    }, 300);
  });
}

function selectItem(index: number) {
  const realIndex = index - offset;
  if (realIndex >= 0 && realIndex < props.items.length) {
    selectedIndex.value = index;
    emit("update:modelValue", props.items[realIndex].value);
  }
}

function onMouseDown(event: MouseEvent) {
  if (event.button !== 0 || !drumRef.value) return;

  isDragging.value = true;
  startPos.value = props.orientation === "vertical" ? event.clientY : event.clientX;
  startScrollPos.value =
    props.orientation === "vertical" ? drumRef.value.scrollTop : drumRef.value.scrollLeft;

  document.addEventListener("mousemove", onMouseMove);
  document.addEventListener("mouseup", onMouseUp);

  event.preventDefault();
}

function onMouseMove(event: MouseEvent) {
  if (!isDragging.value || !drumRef.value) return;

  const currentPos = props.orientation === "vertical" ? event.clientY : event.clientX;
  const delta = startPos.value - currentPos;
  const newScrollPos = startScrollPos.value + delta;

  if (props.orientation === "vertical") {
    drumRef.value.scrollTop = newScrollPos;
  } else {
    drumRef.value.scrollLeft = newScrollPos;
  }
}

function onMouseUp() {
  isDragging.value = false;
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
}

function resetInputBuffer() {
  inputBuffer.value = "";
  if (bufferResetTimeout.value) {
    clearTimeout(bufferResetTimeout.value);
    bufferResetTimeout.value = null;
  }
}

function scheduleBufferReset() {
  if (bufferResetTimeout.value) {
    clearTimeout(bufferResetTimeout.value);
  }
  bufferResetTimeout.value = window.setTimeout(() => {
    inputBuffer.value = "";
    bufferResetTimeout.value = null;
  }, BUFFER_RESET_MS);
}

function findItemIndexByValue(value: string) {
  return props.items.findIndex((item) => String(item.value) === value);
}

function findItemIndexByPrefix(prefix: string) {
  return props.items.findIndex((item) => String(item.value).startsWith(prefix));
}

function isEditableElement(target: EventTarget | null): target is HTMLElement {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

function onKeyDown(event: KeyboardEvent) {
  console.debug("[DrumPicker] keydown", {
    key: event.key,
    targetTag: (event.target as HTMLElement | null)?.tagName,
  });

  if (!drumRef.value || isDragging.value) {
    console.debug("[DrumPicker] skip: no ref or dragging");
    return;
  }
  if (isEditableElement(event.target)) {
    console.debug("[DrumPicker] skip: editable element");
    return;
  }
  if (!/^[0-9]$/.test(event.key)) {
    console.debug("[DrumPicker] skip: non-digit");
    return;
  }

  const nextBuffer = `${inputBuffer.value}${event.key}`;
  const exactIndex = findItemIndexByValue(nextBuffer);

  if (exactIndex !== -1) {
    inputBuffer.value = nextBuffer;
    programmaticScrollTo(exactIndex + offset);
    console.debug("[DrumPicker] match buffer exact", {
      buffer: inputBuffer.value,
      index: exactIndex,
    });
    scheduleBufferReset();
    return;
  }

  const prefixIndex = findItemIndexByPrefix(nextBuffer);
  if (prefixIndex !== -1) {
    inputBuffer.value = nextBuffer;
    programmaticScrollTo(prefixIndex + offset);
    console.debug("[DrumPicker] match buffer prefix", {
      buffer: inputBuffer.value,
      index: prefixIndex,
    });
    scheduleBufferReset();
    return;
  }

  const singleExactIndex = findItemIndexByValue(event.key);
  if (singleExactIndex !== -1) {
    inputBuffer.value = event.key;
    programmaticScrollTo(singleExactIndex + offset);
    console.debug("[DrumPicker] match single digit exact", {
      buffer: inputBuffer.value,
      index: singleExactIndex,
    });
    scheduleBufferReset();
    return;
  }

  const singlePrefixIndex = findItemIndexByPrefix(event.key);
  if (singlePrefixIndex !== -1) {
    inputBuffer.value = event.key;
    programmaticScrollTo(singlePrefixIndex + offset);
    console.debug("[DrumPicker] match single digit prefix", {
      buffer: inputBuffer.value,
      index: singlePrefixIndex,
    });
    scheduleBufferReset();
    return;
  }

  console.debug("[DrumPicker] no match, buffer cleared", {
    attempted: nextBuffer,
  });
  resetInputBuffer();
}

function onWheel(event: WheelEvent) {
  if (!drumRef.value) return;

  const isVertical = props.orientation === "vertical";
  const currentScroll = isVertical ? drumRef.value.scrollTop : drumRef.value.scrollLeft;
  const newScroll = currentScroll + event.deltaY;
  const itemSize = isVertical ? 50 : 46;
  const minScroll = offset * itemSize;
  const maxScroll = (offset + props.items.length - 1) * itemSize;

  if (newScroll < minScroll) {
    programmaticScrollTo(offset);
  } else if (newScroll > maxScroll) {
    programmaticScrollTo(offset + props.items.length - 1);
  }
}

function onScroll() {
  if (!drumRef.value || isProgrammaticScroll.value) return;

  isUserScrolling.value = true;

  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value);
  }

  scrollTimeout.value = setTimeout(() => {
    const items = drumRef.value!.querySelectorAll(".drum-item");
    const containerRect = drumRef.value!.getBoundingClientRect();
    const containerCenter =
      props.orientation === "vertical"
        ? containerRect.top + containerRect.height / 2
        : containerRect.left + containerRect.width / 2;

    let minDistance = Infinity;
    let closestIndex = 0;

    items.forEach((item) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter =
        props.orientation === "vertical"
          ? itemRect.top + itemRect.height / 2
          : itemRect.left + itemRect.width / 2;
      const distance = Math.abs(itemCenter - containerCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = parseInt(item.getAttribute("data-index") || "0");
      }
    });

    const index = closestIndex;
    const realIndex = index - offset;
    if (realIndex < 0) {
      programmaticScrollTo(offset);
    } else if (realIndex >= props.items.length) {
      programmaticScrollTo(offset + props.items.length - 1);
    } else {
      if (index !== selectedIndex.value) {
        selectedIndex.value = index;
        emit("update:modelValue", props.items[realIndex].value);
      }
    }

    setTimeout(() => {
      isUserScrolling.value = false;
    }, 200);
  }, 100);
}

function scrollToSelected() {
  if (!drumRef.value) return;

  isProgrammaticScroll.value = true;

  nextTick(() => {
    scrollIndexIntoView(selectedIndex.value);
    setTimeout(() => {
      isProgrammaticScroll.value = false;
    }, 300);
  });
}

function findInitialIndex() {
  if (!props.modelValue) {
    const middleIndex = Math.floor(props.items.length / 2);
    return offset + middleIndex;
  }

  const index = props.items.findIndex((item) => item.value === props.modelValue);
  return index >= 0 ? index + offset : offset;
}

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal === null) {
      const middleIndex = Math.floor(props.items.length / 2);
      selectedIndex.value = offset + middleIndex;
      if (!isUserScrolling.value) {
        nextTick(() => scrollToSelected());
      }
      return;
    }

    const index = props.items.findIndex((item) => item.value === newVal);
    if (index >= 0) {
      selectedIndex.value = index + offset;
      if (!isUserScrolling.value) {
        nextTick(() => scrollToSelected());
      }
    }
  }
);

watch(
  () => props.items,
  () => {
    selectedIndex.value = findInitialIndex();
    if (!isUserScrolling.value) {
      nextTick(() => scrollToSelected());
    }
  }
);

onMounted(() => {
  selectedIndex.value = findInitialIndex();
  if (!isUserScrolling.value) {
    nextTick(() => scrollToSelected());
  }
  window.addEventListener("keydown", onKeyDown);
});

onBeforeUnmount(() => {
  if (scrollTimeout.value) {
    clearTimeout(scrollTimeout.value);
  }
  if (bufferResetTimeout.value) {
    clearTimeout(bufferResetTimeout.value);
  }
  document.removeEventListener("mousemove", onMouseMove);
  document.removeEventListener("mouseup", onMouseUp);
  window.removeEventListener("keydown", onKeyDown);
});
</script>

<style scoped>
.seletced-value-indicator {
  font-size: 20px;
}

.drum-picker-container {
  position: relative;
  width: 100%;
  height: 316px;
  overflow: hidden;
  background: var(--active-8);
  padding: 10px 0;
  border-radius: 40px;
}

.drum-picker {
  height: 100%;
  overflow-y: auto;
  /* Убрано scroll-behavior: smooth для лучшей производительности */
  scroll-behavior: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  cursor: grab;
  padding: 6px;
}

.drum-picker.dragging {
  cursor: grabbing;
}

.drum-picker::-webkit-scrollbar {
  display: none;
}

.drum-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  font-size: 18px;
  font-weight: 500;
  color: var(--primary-blue-20);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  border-top: 1px solid var(--primary-blue-12);
}

.drum-item:hover {
  background: rgba(0, 122, 255, 0.1);
}

.drum-item-selected {
  display: flex;
  align-items: baseline;
  color: var(--primary-blue-100);
  font-size: 44px;
  font-weight: 600;
  border: none;
  background: var(--white-100);
  height: 66px;
  border-radius: 12px;
  vertical-align: bottom;
}

.drum-item-above,
.drum-item-below {
  color: var(--primary-blue-60);
}

.drum-item-below {
  border: none;
}

.drum-selection-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
  height: 66px;
  border: 1px solid var(--grey-100);
  border-radius: 12px;
  pointer-events: none;
  box-shadow: 0 0 0 1px rgba(0, 122, 255, 0.2);
}

/* Horizontal variant */
.drum-picker-container.horizontal {
  height: 50px;
  width: 100%;
}

.drum-picker-container.horizontal .drum-picker {
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
}

.drum-picker-container.horizontal .drum-item {
  display: inline-flex;
  width: 46px;
  height: 100%;
  flex-shrink: 0;
  border-top: none;
  border-left: 1px solid var(--active-12);
}

.drum-picker-container.horizontal .drum-selection-indicator {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -45%);
  width: 120px;
  height: 70%;
}

.drum-picker-container.horizontal .drum-item-selected {
  width: 120px;
}

/* Smooth scrolling for better UX */
.drum-picker {
  scroll-snap-type: y mandatory;
}

.drum-picker-container.horizontal .drum-picker {
  scroll-snap-type: x mandatory;
}

.drum-item {
  scroll-snap-align: center;
}
</style>
